import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  downloadUserMaster,
  parseUserMasterDownloadResponse,
  previewUserMaster,
  uploadUserMaster,
} from "../../../../api";
import PopoverCustom from "../../../../components/PopoverCustom";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../components/SnackBarCustom";
import type {
  MasterType,
  UserMasterPreviewOkResponse,
} from "../../../../types/user-master";
import { normalizePreviewItem } from "../../../../types/user-master";
import PreviewTable from "./PreviewTable";

const MASTER_TYPE_LABELS: Record<MasterType, string> = {
  com: "COM",
  tok: "特約店",
};

const isWithinSelector = (
  target: EventTarget | null,
  selector: string
): target is Element =>
  target instanceof Element && Boolean(target.closest(selector));

export default function AccountRegistration() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploadInProgressRef = useRef(false);

  const [importMasterType, setImportMasterType] = useState<MasterType | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] =
    useState<UserMasterPreviewOkResponse | null>(null);
  const [previewOk, setPreviewOk] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [downloadPopperOpen, setDownloadPopperOpen] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] =
    useState<HTMLElement | null>(null);
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const previewUsers = (previewData?.users ?? []).map(normalizePreviewItem);
  const previewUsersDelete = (previewData?.users_delete ?? []).map(
    normalizePreviewItem
  );
  const hasError = previewUsers.some((user) => user.status === "error");
  const importButtonDisabled =
    isPreviewLoading || isUploadLoading || (previewOk && hasError);

  const resetPreview = () => {
    setPreviewData(null);
    setPreviewOk(false);
    setUploadModalOpen(false);
  };

  const showSnackbar = (status: SnackbarData) => {
    setSnackBarstatus(status);
    setSnackbarOpen(true);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    resetPreview();
  };

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleImportMasterTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setImportMasterType(event.target.value as MasterType);
    resetPreview();
  };

  const handleDownloadSelect = async (type: MasterType) => {
    setDownloadPopperOpen(false);
    setDownloadAnchorEl(null);
    setIsDownloadLoading(true);
    try {
      const response = await downloadUserMaster(type);
      const result = await parseUserMasterDownloadResponse(response);
      if (result.status === "ng") {
        showSnackbar({ status: "ng", message: result.message });
        return;
      }
      triggerBlobDownload(result.blob, result.filename);
    } catch (error) {
      console.error("API呼び出しエラー", error);
      showSnackbar({
        status: "ng",
        message: "ダウンロードに失敗しました",
      });
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const handleDownloadMouseEnter = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (isDownloadLoading) {
      return;
    }
    setDownloadAnchorEl(event.currentTarget);
    setDownloadPopperOpen(true);
  };

  const handleDownloadMouseLeave = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTimeout(() => {
      const relatedTarget = event.relatedTarget;
      if (
        !isWithinSelector(relatedTarget, '[role="menu"]') &&
        !isWithinSelector(relatedTarget, '[name="downloadButton"]')
      ) {
        setDownloadPopperOpen(false);
        setDownloadAnchorEl(null);
      }
    }, 150);
  };

  const handleImportClick = async () => {
    if (!selectedFile) {
      showSnackbar({
        status: "ng",
        message: "ファイルを指定してください",
      });
      return;
    }
    if (!importMasterType) {
      showSnackbar({
        status: "ng",
        message: "インポートマスタ種別を選択してください",
      });
      return;
    }

    setIsPreviewLoading(true);
    try {
      const response = await previewUserMaster(selectedFile, importMasterType);
      if (response.status !== "ok") {
        showSnackbar(response);
        resetPreview();
        return;
      }

      setPreviewData(response);
      setPreviewOk(true);
      setUploadModalOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
      showSnackbar({
        status: "ng",
        message: "ユーザーマスタプレビューを取得できませんでした",
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleUploadConfirm = async (isConfirmed: boolean) => {
    if (!isConfirmed) {
      return;
    }
    if (isUploadInProgressRef.current) {
      return;
    }
    if (!selectedFile || !importMasterType || hasError) {
      return;
    }

    isUploadInProgressRef.current = true;
    setIsUploadLoading(true);
    try {
      const response = await uploadUserMaster(selectedFile, importMasterType);
      showSnackbar(response);
      if (response.status === "ok") {
        resetPreview();
        clearSelectedFile();
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
      showSnackbar({
        status: "ng",
        message: "ユーザーマスタファイルのアップロードに失敗しました",
      });
    } finally {
      isUploadInProgressRef.current = false;
      setIsUploadLoading(false);
      setUploadModalOpen(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          "@media screen and (max-width:900px)": {
            flexDirection: "column",
            gap: "-16px",
          },
        }}
      >
        <Box
          onClick={handleGoBack}
          sx={{
            display: "flex",
            height: "48px",
            alignItems: "center",
            paddingLeft: "40px",
            fontSize: 20,
            fontWeight: "bold",
            whiteSpace: "nowrap",
            color: "primary.main",
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              color: "primary.dark",
            },
            "&:active": {
              color: "primary.light",
            },
          }}
        >
          アカウント一覧
        </Box>
        <Box
          sx={{
            display: "flex",
            height: "48px",
            alignItems: "center",
            fontSize: 20,
            fontWeight: "bold",
            whiteSpace: "nowrap",
            "@media screen and (max-width:900px)": {
              paddingLeft: "40px",
            },
          }}
        >
          /アカウント発行
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          borderRadius: 2,
          borderColor: "primary.main",
          boxShadow: 3,
          color: "outline.dark",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            gap: "16px",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              paddingLeft: "40px",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索条件
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "12px",
                width: "100%",
                maxWidth: "480px",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Button
                  name="downloadButton"
                  variant="contained"
                  disabled={isDownloadLoading}
                  aria-haspopup="menu"
                  aria-expanded={downloadPopperOpen ? "true" : undefined}
                  onMouseEnter={handleDownloadMouseEnter}
                  onMouseLeave={handleDownloadMouseLeave}
                  sx={{
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  ユーザマスタDL
                  <ArrowDropDownIcon />
                </Button>
                <Popper
                  sx={{ zIndex: 9999 }}
                  open={downloadPopperOpen && Boolean(downloadAnchorEl)}
                  anchorEl={downloadAnchorEl}
                  placement="bottom-start"
                  transition
                  disablePortal={false}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom"
                            ? "center top"
                            : "center bottom",
                      }}
                    >
                      <Paper
                        sx={{ pointerEvents: "auto" }}
                        onMouseEnter={() => setDownloadPopperOpen(true)}
                        onMouseLeave={(event) => {
                          if (
                            !isWithinSelector(
                              event.relatedTarget,
                              '[role="menu"]'
                            )
                          ) {
                            setDownloadPopperOpen(false);
                            setDownloadAnchorEl(null);
                          }
                        }}
                      >
                        <MenuList role="menu" autoFocusItem>
                          <MenuItem
                            {...({
                              name: "downloadComButton",
                            } as React.HTMLAttributes<HTMLLIElement>)}
                            onClick={() => handleDownloadSelect("com")}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "16px",
                            }}
                          >
                            {MASTER_TYPE_LABELS.com}
                            <DownloadIcon color="success" />
                          </MenuItem>
                          <MenuItem
                            {...({
                              name: "downloadTokButton",
                            } as React.HTMLAttributes<HTMLLIElement>)}
                            onClick={() => handleDownloadSelect("tok")}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "16px",
                            }}
                          >
                            {MASTER_TYPE_LABELS.tok}
                            <DownloadIcon color="success" />
                          </MenuItem>
                        </MenuList>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </Box>

              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  sx={{
                    fontSize: 14,
                    color: "outline.dark",
                    "&.Mui-focused": { color: "outline.dark" },
                  }}
                >
                  インポートマスタ種別:
                </FormLabel>
                <RadioGroup
                  row
                  name="importMasterType"
                  value={importMasterType ?? ""}
                  onChange={handleImportMasterTypeChange}
                  sx={{ gap: "8px" }}
                >
                  <FormControlLabel
                    value="com"
                    control={
                      <Radio inputProps={{ name: "importMasterTypeCom" }} />
                    }
                    label="COM"
                  />
                  <FormControlLabel
                    value="tok"
                    control={
                      <Radio inputProps={{ name: "importMasterTypeTok" }} />
                    }
                    label="特約店"
                  />
                </RadioGroup>
              </FormControl>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Button variant="contained" component="label">
                  ファイル選択
                  <input
                    ref={fileInputRef}
                    accept=".xlsx"
                    type="file"
                    name="fileInput"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </Button>
                <Button
                  name="importButton"
                  variant="outlined"
                  disabled={importButtonDisabled}
                  onClick={handleImportClick}
                >
                  <DownloadIcon sx={{ mr: 0.5 }} />
                  インポート
                </Button>
              </Box>

              {selectedFile ? (
                <Tooltip title={selectedFile.name}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box",
                      height: "36px",
                      width: "100%",
                      border: "1px #D9D9D9 solid",
                      borderRadius: "4px",
                    }}
                  >
                    <AttachFileIcon />
                    <Box
                      sx={{
                        height: "32px",
                        width: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {selectedFile.name}
                    </Box>
                  </Box>
                </Tooltip>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    height: "36px",
                    width: "100%",
                    border: "1px #D9D9D9 solid",
                    borderRadius: "4px",
                  }}
                >
                  <AttachFileIcon />
                  ファイルを選択してください
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <PopoverCustom
        open={uploadModalOpen}
        onClose={() => {
          if (!isUploadLoading) {
            setUploadModalOpen(false);
          }
        }}
        yesNo={handleUploadConfirm}
        title="アカウント発行確認"
        contents="下記の内容で発行しますか？"
        yesButtonName="uploadConfirmYes"
        noButtonName="uploadConfirmNo"
        buttonsDisabled={isUploadLoading}
        yesButtonDisabled={hasError}
        closeOnYes={false}
      >
        {previewOk ? (
          <Box sx={{ padding: "0 16px 16px" }}>
            <PreviewTable
              users={previewUsers}
              users_delete={previewUsersDelete}
            />
          </Box>
        ) : null}
      </PopoverCustom>
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
}
